import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Bot,
  User,
  Plus,
  Send,
  AlertTriangle,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react'
import { useReceptionChat } from '@hooks/useReceptionChat'
import { useIsDesktop, useIsPortrait } from '@hooks/useMediaQuery'
import { cn } from '@utils/cn'
import { CustomerDataForm, type CustomerData } from './forms/CustomerDataForm'
import { VehicleDataForm, type VehicleData } from './forms/VehicleDataForm'
import { ExampleAccordion } from './ExampleAccordion'
import { FormModal } from './FormModal'
import { companyService } from '@services/companyService'
import { vehicleService } from '@services/vehicleService'
import { workOrderService } from '@services/workOrderService'
import { useAuthStore } from '@store/useAuthStore'
import { formatCustomerChatLine, formatVehicleChatLine } from '@utils/receptionChatFormatting'
import { SchedulingModal } from './SchedulingModal'
import { formatDateTime } from '@utils/format'
import { ConfirmDeleteDraftModal } from './ConfirmDeleteDraftModal'

interface ReceptionChatInteractiveProps {
  readonly conversationId?: number
  readonly className?: string
}

export function ReceptionChatInteractive({ conversationId, className }: Readonly<ReceptionChatInteractiveProps>) {
  const isPortrait = useIsPortrait()
  const isDesktop = useIsDesktop()
  const {
    messages,
    sendMessage,
    isSending,
    isLoadingConversation,
    isBackgroundSync,
    syncError,
    loadConversation,
    // concludeConversation pode ser usado no futuro
    // concludeConversation,
    loadDrafts,
    drafts,
    resetToNew,
    startScheduling,
    isScheduling,
    scheduledAt,
    conversationId: currentId,
    workOrderId,
    workOrderNumber,
    status,
    deleteDraft,
  } = useReceptionChat(conversationId)

  const [input, setInput] = useState('')
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [vehicleData, setVehicleData] = useState<VehicleData | null>(null)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [showSchedulingModal, setShowSchedulingModal] = useState(false)
  const [draftsOpen, setDraftsOpen] = useState(isDesktop)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showActiveWorkOrderDialog, setShowActiveWorkOrderDialog] = useState(false)
  const [activeWorkOrders, setActiveWorkOrders] = useState<any[]>([])
  // pendingVehicleId é usado apenas para setPendingVehicleId, não para leitura
  const [, setPendingVehicleId] = useState<number | null>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const { user } = useAuthStore()

  useEffect(() => {
    setTimeout(() => {
      endRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }, [messages, isSending, isBackgroundSync])

  useEffect(() => {
    loadDrafts()
  }, [loadDrafts])

  useEffect(() => {
    setDraftsOpen(isDesktop)
  }, [isDesktop])

  useEffect(() => {
    if (conversationId !== undefined && conversationId !== currentId) {
      loadConversation(conversationId)
    }
  }, [conversationId, currentId, loadConversation])

  // Detectar o tipo de pergunta atual baseado na última mensagem do assistente
  // IMPORTANTE: Usar as descrições exatas das perguntas do banco de dados (DevDataInitializer)
  // para garantir que os formulários apareçam apenas nas perguntas corretas
  // CRÍTICO: Considerar também o followUp, pois a próxima pergunta pode estar lá
  const lastAssistantMessage = useMemo(() => {
    if (!messages || messages.length === 0) {
      return null
    }
    
    // Filtrar mensagens do assistente (incluindo followUp que tem position decimal)
    const assistantMessages = messages
      .filter(m => m && m.role === 'assistant' && m.content)
      .sort((a, b) => (a.position || 0) - (b.position || 0))
    
    return assistantMessages.length > 0 ? assistantMessages[assistantMessages.length - 1] : null
  }, [messages])

  const hasAnsweredMaintenanceOrBreakdown = useMemo(() => {
    if (!messages || messages.length === 0) return false

    const isChoiceAnswer = (content: string) => {
      const c = content.toLowerCase().trim()
      return (
        c === 'manutenção' ||
        c === 'manutencao' ||
        c === 'avaria' ||
        c.includes('manutencao') ||
        c.includes('manutenção') ||
        (c.includes('avaria') && !c.includes('manutenção') && !c.includes('manutencao'))
      )
    }

    return messages.some(
      (m) =>
        m?.role === 'user' &&
        m.content?.trim() &&
        isChoiceAnswer(m.content) &&
        Math.floor(m.position ?? 0) >= 2
    )
  }, [messages])

  // Detectar qual pergunta está sendo exibida atualmente
  const currentQuestionId = useMemo(() => {
    if (!lastAssistantMessage?.content) {
      return null
    }

    const content = lastAssistantMessage.content.toLowerCase()

    // Conteúdo tem prioridade sobre position (follow-up usa posições decimais)
    if (content.includes('sintomas') && content.includes('experienciou')) {
      return 5
    }
    if (content.includes('local do carro') || content.includes('problema recente')) {
      return 6
    }
    if (content.includes('mais alguma informação')) {
      return 7
    }
    if (content.includes('tipo de serviço de manutenção')) {
      return 4
    }
    if (
      content.includes('barulho que mencionou') ||
      content.includes('vibração que mencionou') ||
      content.includes('vibracao que mencionou') ||
      content.includes('luzes que mencionou') ||
      content.includes('problema do motor') ||
      content.includes('problema dos travões') ||
      content.includes('problema da direção') ||
      content.includes('problema da direcao') ||
      content.includes('problema da suspensão') ||
      content.includes('problema da suspensao') ||
      content.includes('problema da transmissão') ||
      content.includes('problema elétrico') ||
      content.includes('problema eletrico') ||
      content.includes('problema de aquecimento') ||
      content.includes('fumo que mencionou') ||
      content.includes('consumo excessivo')
    ) {
      return 10
    }

    if (lastAssistantMessage.position !== undefined) {
      const pos = Math.floor(lastAssistantMessage.position)
      if (pos === 0) return 1
      if (pos === 1) return 2
      if (pos === 3 && !hasAnsweredMaintenanceOrBreakdown) return 3
    }
    
    const contentLower = content
    
    // Pergunta 1: dados pessoais do cliente
    if (contentLower.includes('para iniciar a receção') ||
        contentLower.includes('dados pessoais do cliente') ||
        (contentLower.includes('nome do cliente') && contentLower.includes('obrigatório'))) {
      return 1
    }
    
    // Pergunta 2: dados do veículo
    if (contentLower.includes('dados do carro') ||
        contentLower.includes('dados do veículo') ||
        contentLower.includes('dados do veiculo') ||
        (contentLower.includes('matrícula') && contentLower.includes('marca'))) {
      return 2
    }
    
    // Pergunta 3: manutenção ou avaria (só se ainda não respondeu)
    if (
      !hasAnsweredMaintenanceOrBreakdown &&
      (contentLower.includes('é manutenção ou avaria') ||
        contentLower.includes('manutenção ou avaria') ||
        contentLower.includes('manutencao ou avaria') ||
        (contentLower.includes('manutenção') && contentLower.includes('avaria')) ||
        (contentLower.includes('manutencao') && contentLower.includes('avaria')))
    ) {
      return 3
    }
    
    return null
  }, [lastAssistantMessage, hasAnsweredMaintenanceOrBreakdown])

  // Verificar se a última pergunta é a pergunta 1 (dados pessoais do cliente)
  // IMPORTANTE: Apenas retornar true se currentQuestionId === 1 (exclusivo da pergunta 1)
  const isCustomerQuestion = useMemo(() => {
    return currentQuestionId === 1
  }, [currentQuestionId])

  // Verificar se a última pergunta é a pergunta 2 (dados do carro)
  // IMPORTANTE: Apenas retornar true se currentQuestionId === 2 (exclusivo da pergunta 2)
  const isVehicleQuestion = useMemo(() => {
    return currentQuestionId === 2
  }, [currentQuestionId])

  const isMaintenanceOrBreakdownQuestion = useMemo(() => {
    // Não mostrar botões se o usuário já respondeu
    if (hasAnsweredMaintenanceOrBreakdown) {
      return false
    }
    
    // Verificar primeiro pelo position - se a última mensagem do assistente tem position === 3 (ou próximo), é a pergunta 3
    if (lastAssistantMessage?.position !== undefined) {
      const pos = Math.floor(lastAssistantMessage.position)
      if (pos === 3) {
        return true
      }
    }
    
    // Sempre mostrar botões se currentQuestionId for 3 (pergunta 3)
    // Isso garante que os botões apareçam sempre que a última pergunta for a 3
    if (currentQuestionId === 3) {
      return true
    }
    
    // Fallback: verificar pelo conteúdo da última mensagem do assistente
    if (!lastAssistantMessage?.content) {
      return false
    }
    
    const content = lastAssistantMessage.content.toLowerCase().trim()
    
    // Detectar pela descrição exata da pergunta 3 do banco de dados: "É manutenção ou avaria?"
    // Tornar detecção mais flexível e robusta
    const isQuestion3 = content === 'é manutenção ou avaria?' || 
           content === 'é manutenção ou avaria' ||
           content === 'é manutencao ou avaria?' ||
           content === 'é manutencao ou avaria' ||
           content.includes('é manutenção ou avaria') || 
           content.includes('é manutencao ou avaria') ||
           content.includes('manutenção ou avaria') ||
           content.includes('manutencao ou avaria') ||
           (content.includes('manutenção') && content.includes('avaria')) ||
           (content.includes('manutencao') && content.includes('avaria'))
    
    return isQuestion3
  }, [currentQuestionId, lastAssistantMessage, hasAnsweredMaintenanceOrBreakdown])

  // Modais não abrem automaticamente - apenas quando o usuário clicar no botão

  const handleSend = async () => {
    if (!input.trim() || isSending || isBackgroundSync) return
    const messageText = input.trim()
    setInput('')
    await sendMessage(messageText)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleCustomerSubmit = async (data: CustomerData) => {
    setCustomerData(data)
    setShowCustomerModal(false)
    // Formatar dados do cliente para enviar ao backend
    const customerText = formatCustomerChatLine(data)
    
    // Se não há conversationId ainda, criar conversa primeiro
    if (!currentId) {
      // Criar conversa no backend com os dados do cliente
      await sendMessage(customerText)
    } else {
      // Se já há conversa, apenas enviar mensagem
      await sendMessage(customerText)
    }
    setInput('')
  }

  const handleNifSearch = async (nif: string): Promise<CustomerData | null> => {
    try {
      // Buscar cliente por NIF no backend (retorna dados completos incluindo email e telefone)
      const response = await companyService.getByNIF(nif)
      
      if (!response) {
        return null
      }
      
      // O backend retorna um Map com todos os dados, incluindo nif, email, phone extraídos das work orders
      return {
        name: response.name || '',
        email: (response as any).email || '',
        phone: (response as any).phone || '',
        nif: (response as any).nif || nif,
      }
    } catch (error) {
      console.error('Erro ao pesquisar cliente por NIF:', error)
      return null
    }
  }

  const handleRegistrationPlateSearch = async (registrationPlate: string): Promise<VehicleData | null> => {
    try {
      const vehicle = await vehicleService.getByRegistrationPlate(registrationPlate)
      
      if (!vehicle) {
        return null
      }
      
      // Converter dados do veículo para o formato do formulário
      return {
        matricula: vehicle.registrationPlate,
        marca: vehicle.brand || '',
        modelo: vehicle.model || '',
        ano: vehicle.year?.toString() || '',
        km: '',
        combustivel: vehicle.fuelType || '',
        ultimaInspecao: vehicle.lastRevisionDate?.slice(0, 10) || '',
      }
    } catch (error) {
      console.error('Erro ao pesquisar veículo por matrícula:', error)
      return null
    }
  }

  const checkActiveWorkOrders = async (vehicleId: number) => {
    try {
      const workshopCompanyId = user?.workshopCompanyId
      if (workshopCompanyId == null) return []
      
      // Usar o endpoint específico para folhas ativas por veículo
      const response = await workOrderService.getAll({
        vehicleId,
        workshopCompanyId,
      })
      
      // Filtrar apenas folhas ativas (status não finalizados)
      const activeStatuses = ['PENDING', 'DRAFT', 'IN_PROGRESS', 'WAITING_PARTS', 'WAITING_APPROVAL']
      return (response || []).filter((wo: any) => activeStatuses.includes(wo.status))
    } catch (error) {
      console.error('Erro ao verificar folhas de obra ativas:', error)
      return []
    }
  }

  const handleVehicleSubmit = async (data: VehicleData) => {
    setVehicleData(data)
    
    // Buscar veículo por matrícula para obter o ID
    try {
      const vehicle = await vehicleService.getByRegistrationPlate(data.matricula)
      if (vehicle) {
        // vehicleId pode ser usado no futuro
        // const vehicleId = vehicle.id
        
        // Verificar se há folhas de obra ativas para este veículo
        const activeOrders = await checkActiveWorkOrders(vehicle.id)
        
        if (activeOrders.length > 0) {
          // Mostrar diálogo de confirmação
          setActiveWorkOrders(activeOrders)
          setPendingVehicleId(vehicle.id)
          setShowActiveWorkOrderDialog(true)
          setShowVehicleModal(false)
          return // Não continuar até o utilizador confirmar
        }
      }
    } catch (error) {
      console.error('Erro ao verificar veículo ou folhas ativas:', error)
    }
    
    // Se não há folhas ativas ou veículo não encontrado, continuar normalmente
    setShowVehicleModal(false)
    
    // Formatar dados do veículo para enviar ao backend
    const vehicleText = formatVehicleChatLine(data)
    
    // Se não há conversationId ainda, criar conversa primeiro
    if (!currentId) {
      // Primeiro enviar dados do cliente (se ainda não foram enviados)
      if (customerData) {
        const customerText = formatCustomerChatLine(customerData)
        await sendMessage(customerText)
      }
      // Depois enviar dados do veículo
      await sendMessage(vehicleText)
    } else {
      // Se já há conversa, apenas enviar mensagem
      await sendMessage(vehicleText)
    }
    setInput('')
  }

  const handleContinueWithNewWorkOrder = async () => {
    setShowActiveWorkOrderDialog(false)
    
    if (!vehicleData) return
    
    // Continuar com a criação da nova folha de obra
    const vehicleText = formatVehicleChatLine(vehicleData)
    
    if (!currentId) {
      if (customerData) {
        const customerText = formatCustomerChatLine(customerData)
        await sendMessage(customerText)
      }
      await sendMessage(vehicleText)
    } else {
      await sendMessage(vehicleText)
    }
    
    setActiveWorkOrders([])
    setPendingVehicleId(null)
    setInput('')
  }

  const handleCancelNewWorkOrder = () => {
    setShowActiveWorkOrderDialog(false)
    setActiveWorkOrders([])
    setPendingVehicleId(null)
    setVehicleData(null)
    setShowVehicleModal(true) // Voltar ao modal para permitir edição
  }

  const handleSelectionClick = async (selection: 'Manutenção' | 'Avaria') => {
    if (isSending || isBackgroundSync) return
    await sendMessage(selection === 'Avaria' ? 'avaria' : 'manutencao')
    setInput('')
  }

  const handleDraftSelect = async (id: number) => {
    try {
      await loadConversation(id)
      if (!isDesktop) {
        setDraftsOpen(false)
      }
    } catch (error) {
      console.error('Erro ao carregar conversa:', error)
    }
  }

  const openDeleteModal = (id: number) => {
    setDeleteTargetId(id)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    const target = deleteTargetId
    if (!target) return
    try {
      await deleteDraft(target)
      setShowDeleteConfirm(false)
      setDeleteTargetId(null)
      if (!isDesktop) {
        // Keep the drafts panel open so user sees the updated list
        setDraftsOpen(true)
      }
    } catch {
      // error is shown via syncError banner (hook)
    }
  }

  const draftsPanelContent = (
    <>
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <span className="text-sm font-semibold text-secondary-800">Conversas (rascunho)</span>
        <button
          type="button"
          onClick={loadDrafts}
          className="text-xs text-primary-600 hover:text-primary-700"
        >
          Atualizar
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {drafts.length === 0 ? (
          <p className="px-4 py-3 text-sm text-secondary-500">Sem conversas em rascunho.</p>
        ) : (
          drafts.map((conv) => (
            <div
              key={conv.conversationId}
              className={cn(
                'flex items-stretch border-b border-gray-100 transition-colors hover:bg-secondary-50',
                conv.conversationId === currentId && 'bg-secondary-50'
              )}
            >
              <button
                type="button"
                onClick={() => handleDraftSelect(conv.conversationId)}
                disabled={isLoadingConversation}
                className={cn(
                  'min-w-0 flex-1 px-4 py-3 text-left',
                  isLoadingConversation && 'cursor-wait opacity-50'
                )}
              >
                <div className="text-sm font-semibold text-secondary-900">#{conv.conversationId}</div>
                <div className="text-xs text-secondary-600">{conv.lastMessage || 'Sem mensagens'}</div>
                <div className="mt-1 text-[11px] text-secondary-400">
                  Status: {conv.status === 0 ? 'A decorrer' : 'Concluído'}
                  {conv.changedBy ? ` · ${conv.changedBy}` : ''}
                </div>
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  openDeleteModal(conv.conversationId)
                }}
                disabled={isLoadingConversation}
                aria-label={`Eliminar conversa #${conv.conversationId}`}
                className={cn(
                  'group flex w-12 items-center justify-center border-l border-gray-100 text-secondary-700 transition-colors',
                  'hover:text-red-600',
                  isLoadingConversation && 'cursor-wait opacity-50'
                )}
                title="Eliminar rascunho"
              >
                <X
                  className={cn(
                    'h-4 w-4 transition-transform duration-150 group-hover:scale-[1.2]'
                  )}
                />
              </button>
            </div>
          ))
        )}
      </div>
    </>
  )

  return (
    <div className={cn('relative flex h-full overflow-hidden rounded-lg bg-white shadow-sm', className)}>
      {!isDesktop && draftsOpen && (
        <button
          type="button"
          className="fixed inset-0 z-10 bg-black/20"
          onClick={() => setDraftsOpen(false)}
          aria-label="Fechar rascunhos"
        />
      )}
      <div className="relative z-0 flex min-w-0 flex-1 flex-col">
        <div
          className={cn(
            'flex justify-between gap-2 border-b border-gray-200 px-4 py-3',
            isPortrait ? 'items-start' : 'items-center'
          )}
        >
          <div className="text-sm text-gray-600 min-w-0 flex-1">
            {currentId ? `Conversa #${currentId}` : isScheduling ? 'Nova marcação' : 'Nova folha de obra'}
            {isScheduling && scheduledAt && (
              <span className="ml-2 text-amber-700 font-medium">
                · Agendado: {formatDateTime(scheduledAt)}
              </span>
            )}
            {workOrderNumber && (
              <span className="ml-2 text-primary-600 font-semibold">
                · Folha de obra: {workOrderNumber}
              </span>
            )}
          </div>
          <div
            className={cn(
              'flex shrink-0 gap-2',
              isPortrait && 'flex-col items-end'
            )}
          >
            {/* Botão Concluir comentado temporariamente */}
            {/* <button
              onClick={() => concludeConversation(currentId)}
              disabled={!currentId || isLoading}
              className="inline-flex items-center gap-2 rounded-md border border-green-200 px-3 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Concluir
            </button> */}
            <button
              type="button"
              onClick={() => setShowSchedulingModal(true)}
              className={cn(
                'inline-flex items-center justify-center gap-2 rounded-md border border-primary-200 text-primary-700 hover:bg-primary-50',
                isPortrait ? 'px-3.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
              )}
            >
              <Calendar className="w-4 h-4 shrink-0" />
              {isPortrait ? 'Marcação' : 'Fazer marcação'}
            </button>
            <button
              type="button"
              onClick={() => {
                setInput('')
                setCustomerData(null)
                setVehicleData(null)
                setShowCustomerModal(false)
                setShowVehicleModal(false)
                setShowSchedulingModal(false)
                resetToNew()
              }}
              className={cn(
                'inline-flex items-center justify-center gap-2 rounded-md border border-secondary-200 text-secondary-700 hover:bg-secondary-50',
                isPortrait ? 'px-3.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
              )}
            >
              <Plus className="w-4 h-4 shrink-0" />
              {isPortrait ? 'Conversa' : 'Nova conversa'}
            </button>

            {currentId && status === 0 && !workOrderId ? (
              <button
                type="button"
                onClick={() => openDeleteModal(currentId)}
                disabled={isSending || isLoadingConversation || isBackgroundSync}
                className={cn(
                  'inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
                  isPortrait ? 'px-3.5 py-1.5 text-xs' : 'px-3 py-2 text-sm',
                  (isSending || isLoadingConversation || isBackgroundSync) && 'opacity-50'
                )}
              >
                <X className="w-4 h-4 shrink-0" />
                Eliminar conversa
              </button>
            ) : null}
          </div>
        </div>

        {(isBackgroundSync || syncError) && (
          <div
            role="status"
            className={cn(
              'mx-4 mt-2 rounded-lg px-3 py-2 text-sm flex items-center gap-2',
              syncError
                ? 'bg-amber-50 text-amber-900 border border-amber-200'
                : 'bg-primary-50 text-primary-800 border border-primary-100'
            )}
          >
            {isBackgroundSync && (
              <span className="inline-block h-2 w-2 rounded-full bg-primary-500 animate-pulse shrink-0" />
            )}
            <span>
              {syncError ??
                'A processar a sua resposta. Pode continuar a navegar — a conversa está a ser guardada.'}
            </span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Mensagens do chat */}
          {messages.map((message, idx) => {
            // Não mostrar mensagens de usuário vazias (respostas simuladas)
            if (message.role === 'user' && !message.content.trim()) {
              return null
            }
            
            return (
              <div
                key={`${message.position}-${idx}-${message.role}-${message.content.substring(0, 20)}`}
                className={cn('flex items-start space-x-3', {
                  'flex-row-reverse space-x-reverse': message.role === 'user',
                })}
              >
                <div
                  className={cn('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center', {
                    'bg-primary-600 text-white': message.role === 'assistant',
                    'bg-secondary-600 text-white': message.role === 'user',
                  })}
                >
                  {message.role === 'assistant' ? <Bot className="w-5 h-5" /> : <User className="w-5 h-5" />}
                </div>
                <div
                  className={cn('flex-1 rounded-lg px-4 py-2 max-w-[80%] whitespace-pre-wrap', {
                    'bg-primary-50 text-primary-900': message.role === 'assistant',
                    'bg-secondary-100 text-secondary-900': message.role === 'user',
                    'opacity-70': message.role === 'user' && message.pending,
                  })}
                >
                  {message.role === 'assistant' ? (
                    <ExampleAccordion 
                      content={message.content} 
                      messageKey={`${message.position}-${message.role}-${message.content.substring(0, 50).replaceAll(/\s+/g, '-')}`}
                    />
                  ) : (
                    <p className="text-sm">{message.content}</p>
                  )}
                </div>
              </div>
            )
          })}

          {/* Formulários removidos - agora aparecem apenas no modal */}


          <div ref={endRef} />
        </div>

        {/* Campo de entrada de texto */}
        <div className="border-t border-gray-200 p-4">
          {/* Mostrar mensagem de loading apenas se não houver mensagens ainda */}
          {isLoadingConversation && (!messages || messages.length === 0) && (
            <div className="text-center text-sm text-gray-500 py-4">
              A carregar conversa...
            </div>
          )}
          
          {/* Nota: não bloquear o input quando uma folha de obra já existe.
              A folha pode ser criada a meio do fluxo (ex: manutenção) e o chat deve continuar. */}
            {isMaintenanceOrBreakdownQuestion ? (
              // Mostrar botões de seleção quando a pergunta sobre manutenção/avaria aparecer
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-2">Selecione uma opção:</p>
                <div className="flex gap-3">
                  <button
                    id="maintenance-button"
                    onClick={() => handleSelectionClick('Manutenção')}
                    disabled={isSending || isBackgroundSync}
                    className={cn(
                      'flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                      { 'cursor-pointer': !isSending && !isBackgroundSync }
                    )}
                  >
                    Manutenção
                  </button>
                  <button
                    id="breakdown-button"
                    onClick={() => handleSelectionClick('Avaria')}
                    disabled={isSending || isBackgroundSync}
                    className={cn(
                      'flex-1 px-6 py-4 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                      { 'cursor-pointer': !isSending && !isBackgroundSync }
                    )}
                  >
                    Avaria
                  </button>
                </div>
              </div>
            ) : isCustomerQuestion ? (
              // Mostrar apenas botão para abrir modal de dados do cliente
              <button
                id="customer-form-button"
                data-testid="customer-form-button"
                onClick={() => setShowCustomerModal(true)}
                disabled={isSending || isBackgroundSync}
                className="w-full bg-primary-600 text-white rounded-lg px-6 py-3 text-base font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Preencher dados do cliente
              </button>
            ) : isVehicleQuestion ? (
              // Mostrar apenas botão para abrir modal de dados do veículo
              <button
                id="vehicle-form-button"
                data-testid="vehicle-form-button"
                onClick={() => setShowVehicleModal(true)}
                disabled={isSending || isBackgroundSync}
                className="w-full bg-primary-600 text-white rounded-lg px-6 py-3 text-base font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Preencher dados do veículo
              </button>
            ) : (
              // Campo de texto normal para outras mensagens
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escreva aqui..."
                  disabled={isSending || isBackgroundSync}
                  className="flex-1 bg-[#17253C] text-white rounded-lg px-6 py-4 text-lg placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/60 transition-all disabled:opacity-50"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isSending || isBackgroundSync}
                  className={cn(
                    'p-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                    { 'cursor-pointer': input.trim() && !isSending && !isBackgroundSync }
                  )}
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        
        {/* Mensagem de sucesso quando folha de obra foi criada */}
        {workOrderId && workOrderNumber && (
          <div className="border-t border-gray-200 p-4 bg-green-50">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm font-semibold text-green-900">
                Folha de obra {workOrderNumber} criada com sucesso!
              </p>
            </div>
            <p className="text-xs text-green-700">
              A conversa foi concluída. A folha de obra está pronta para ser atribuída a um mecânico.
            </p>
          </div>
        )}
      </div>

      {isDesktop ? (
        <aside className="flex w-72 shrink-0 flex-col border-l border-gray-200 bg-white">
          {draftsPanelContent}
        </aside>
      ) : (
        <div
          className={cn(
            'fixed top-16 right-0 bottom-0 z-20 flex items-center transition-transform duration-300 ease-in-out',
            draftsOpen ? 'translate-x-0' : 'translate-x-[calc(100%-0.625rem)]'
          )}
        >
          <button
            type="button"
            onClick={() => setDraftsOpen((open) => !open)}
            className="flex min-h-36 w-2.5 shrink-0 items-center justify-center rounded-l-md bg-primary-600 text-white shadow-md hover:bg-primary-700"
            aria-label={draftsOpen ? 'Fechar rascunhos' : 'Abrir rascunhos'}
            aria-expanded={draftsOpen}
          >
            {draftsOpen ? (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
            )}
          </button>
          <aside
            className={cn(
              'flex h-full w-72 flex-col bg-white shadow-xl',
              !draftsOpen && 'pointer-events-none'
            )}
          >
            {draftsPanelContent}
          </aside>
        </div>
      )}

      {/* Modais para perguntas 1 e 2 */}
      <FormModal
        id="customer-data-modal"
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
      >
        <CustomerDataForm
          onSubmit={handleCustomerSubmit}
          onNifSearch={handleNifSearch}
          isLoading={isSending || isLoadingConversation}
          initialData={customerData || undefined}
          onClose={() => setShowCustomerModal(false)}
        />
      </FormModal>

      <FormModal
        id="vehicle-data-modal"
        isOpen={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
      >
        <VehicleDataForm
          onSubmit={handleVehicleSubmit}
          onRegistrationPlateSearch={handleRegistrationPlateSearch}
          isLoading={isSending || isLoadingConversation}
          initialData={vehicleData || undefined}
          onClose={() => setShowVehicleModal(false)}
        />
      </FormModal>

      {/* Dialog de confirmação para folhas de obra ativas */}
      {showActiveWorkOrderDialog && activeWorkOrders.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Folha de obra ativa encontrada
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              Já existe uma folha de obra aberta para este veículo. Deseja abrir uma nova?
            </p>
            <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
              {activeWorkOrders.map((wo) => (
                <div key={wo.id} className="p-2 bg-gray-50 rounded border border-gray-200">
                  <div className="text-sm font-medium text-gray-900">{wo.workOrderNumber}</div>
                  <div className="text-xs text-gray-500">
                    Status: {wo.status} · Criada em: {new Date(wo.createdAt).toLocaleDateString('pt-PT')}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelNewWorkOrder}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleContinueWithNewWorkOrder}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Abrir nova folha
              </button>
            </div>
          </div>
        </div>
      )}

      <SchedulingModal
        open={showSchedulingModal}
        onClose={() => setShowSchedulingModal(false)}
        isLoading={isSending || isLoadingConversation}
        onConfirm={async (scheduledAtIso) => {
          try {
            setShowSchedulingModal(false)
            setInput('')
            setCustomerData(null)
            setVehicleData(null)
            await startScheduling(scheduledAtIso)
          } catch {
            setShowSchedulingModal(true)
          }
        }}
      />

      <ConfirmDeleteDraftModal
        open={showDeleteConfirm}
        conversationId={deleteTargetId}
        isLoading={isSending || isLoadingConversation}
        onCancel={() => {
          if (isSending || isLoadingConversation) return
          setShowDeleteConfirm(false)
          setDeleteTargetId(null)
        }}
        onConfirm={confirmDelete}
      />
    </div>
  )
}